import { AssignmentCancelTypeEnum, AssignmentDeviationEnum, AssignmentStatusEnum, AssignmentViewModel, CaseModel, CaseTypeEnum, StayOverTypeEnum } from 'api/model';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import Select from 'components/inputs/Select';
import Modal, { modalContentClass } from 'components/Modal';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { alphabeticCompare } from 'utils/sorting';
import useTranslations from 'contexts/basicData/useTranslations';
import * as yup from 'yup';
import { useFormWithSchema } from 'hooks/useFormWithSchema';
import { toInputDateTimeString } from 'utils/date-helpers';
import Checkbox from 'components/inputs/Checkbox';
import { isAfter, startOfDay } from 'date-fns';
import {
  arrivedCancelTypesBlockedByLinkedHomeDelivery,
  getOtherAssignmentIdsToCancelByDefault,
} from './CaseAssignments/utils';

const MyModal = styled(Modal)`
  .${modalContentClass} {
    display: flex;
    flex-direction: column;
    gap: 10px;

    select,
    textarea,
    input {
      width: auto;
    }
    textarea {
      min-width: 300px;
      min-height: 2em;
    }
  }
`;

const Info = styled.div`
  max-width: 40vw;
`;

interface CancelFormState {
  cancelType: AssignmentCancelTypeEnum;
  endTime?: Date;
  comment?: string;
}

const HIDDEN_CANCEL_TYPES: AssignmentCancelTypeEnum[] = [
  AssignmentCancelTypeEnum.CanceledRebooked,
  AssignmentCancelTypeEnum.CanceledOther,
];

const allCancelTypes: AssignmentCancelTypeEnum[] = Object.values(
  AssignmentCancelTypeEnum
).filter((v) => !HIDDEN_CANCEL_TYPES.includes(v));

interface Props {
  assignmentViewModel: AssignmentViewModel;
  linkedCases?: CaseModel[];
  onOkClick(
    assignmentStatus: AssignmentStatusEnum,
    assignmendDeviation: AssignmentDeviationEnum,
    cancelType: AssignmentCancelTypeEnum,
    closeCase: boolean,
    clearFixedAssignmentTime: boolean,
    otherAssignmentIds: number[],
    endTime?: Date,
    comment?: string,
    standbyEligible?: boolean,
    linkedTradeInCaseToCancel?: { caseId: number; assignmentId: number },
    keepStayOver?: boolean
  ): void;
  onCloseClick(): void;
}

const CancelCaseAssignmentModal: FC<Props> = ({
  assignmentViewModel,
  linkedCases,
  onCloseClick,
  onOkClick,
}) => {
  const otherAssignmentsWithinSameCase = useMemo(
    () =>
      assignmentViewModel.case.assignmentList.filter(
        (a) =>
          a.assignmentID !== assignmentViewModel.assignment.assignmentID &&
          a.assignmentDeviationID === AssignmentDeviationEnum.None
      ),
    [
      assignmentViewModel.case.assignmentList,
      assignmentViewModel.assignment.assignmentID,
    ]
  );
  // Förvalt bara när huvuduppdraget avbokas
  const [selectedOtherAssignmentIds, setSelectedOtherAssignmentIds] = useState<
    number[]
  >(() =>
    getOtherAssignmentIdsToCancelByDefault(
      assignmentViewModel.case,
      assignmentViewModel.assignment.assignmentID,
      otherAssignmentsWithinSameCase
    )
  );

  const [isCancelling, setIsCancelling] = useState(false);
  // Avbok ej anlänt/bomkörning: ska övernattningen (nattraktamente i löneexporten) stå kvar?
  const [keepStayOver, setKeepStayOver] = useState<boolean | undefined>();

  // Hitta kopplat TradeIn-ärende med 1 uppdrag och samma FT
  const linkedTradeInToCancel = useMemo(() => {
    if (assignmentViewModel.case.caseTypeID !== CaseTypeEnum.HomeDelivery)
      return null;
    if (!linkedCases) return null;

    const currentFtId = assignmentViewModel.assignment.assignedTo?.userID;
    if (!currentFtId) return null;

    const tradeIn = linkedCases.find(
      (c) =>
        c.caseTypeID === CaseTypeEnum.TradeIn &&
        c.assignmentList.length === 1 &&
        c.assignmentList[0].assignedTo?.userID === currentFtId &&
        c.assignmentList[0].assignmentDeviationID ===
          AssignmentDeviationEnum.None
    );

    return tradeIn ?? null;
  }, [assignmentViewModel, linkedCases]);

  const { cancelTypes, stayOverTypes } = useTranslations();
  const validationSchema = yup.object<CancelFormState>().shape({
    cancelType: yup.number().required(),
    endTime: yup.date().when('cancelType', (cancelType, schema) => {
      return (
        [
        AssignmentCancelTypeEnum.CanceledArrived,
        AssignmentCancelTypeEnum.CanceledNotArrived,
        ] as AssignmentCancelTypeEnum[]
      ).includes(Number(cancelType) as AssignmentCancelTypeEnum)
        ? schema.notRequired()
        : schema.required('Sluttid måste anges');
    }),
    comment: yup.string().when('cancelType', (cancelType, schema) => {
      return ([AssignmentCancelTypeEnum.CanceledOther] as AssignmentCancelTypeEnum[]).includes(
        cancelType as unknown as AssignmentCancelTypeEnum
      )
        ? schema.required('Skriv anledning till avbokning i kommentarsrutan')
        : schema.notRequired();
    }),
    standbyEligible: yup.boolean().when('cancelType', (cancelType, schema) => {
      const isStandbyEligibleType = (
        [
        AssignmentCancelTypeEnum.CanceledNotStartedSameDay,
        AssignmentCancelTypeEnum.CanceledNotArrived,
        AssignmentCancelTypeEnum.CanceledArrived,
        ] as AssignmentCancelTypeEnum[]
      ).includes(Number(cancelType) as unknown as AssignmentCancelTypeEnum);

      return isStandbyEligibleType
        ? schema.required('Standby-tid måste anges')
        : schema.notRequired();
    }),
  });

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormWithSchema<typeof validationSchema>(validationSchema, {
    defaultValues: {
      cancelType: -1,
      endTime: assignmentViewModel.assignment.endTime ?? new Date(),
      comment: '',
      // false if trade-in case, undefined otherwise
      standbyEligible:
        assignmentViewModel.case.caseTypeID === CaseTypeEnum.TradeIn
          ? false
          : undefined,
    },
  });

  const isTradeInCase =
    assignmentViewModel.case.caseTypeID === CaseTypeEnum.TradeIn;

  // "Avbok ej anlänt"/"Bom anlänt" får inte sättas på ett inbyte vars kopplade
  // hemleverans är avbokad (oavsett hemleveransens avbokningstyp). Backend blockerar också.
  const blockArrivedCancelTypes = useMemo(
    () =>
      arrivedCancelTypesBlockedByLinkedHomeDelivery(
        assignmentViewModel.case.caseTypeID,
        linkedCases
      ),
    [assignmentViewModel.case.caseTypeID, linkedCases]
  );

  const shouldHaveStandbyEligibleSet =
    !isTradeInCase &&
    (
    [
      AssignmentCancelTypeEnum.CanceledNotStartedSameDay,
      AssignmentCancelTypeEnum.CanceledNotArrived,
      AssignmentCancelTypeEnum.CanceledArrived,
      ] as AssignmentCancelTypeEnum[]
    ).includes(watch('cancelType') as unknown as AssignmentCancelTypeEnum);

  const state = watch();

  // Uppdragen som avbokas och har övernattning Hotell/Privat (ger nattraktamente)
  const legsWithStayOverBeingCanceled = useMemo(() => {
    const ids = [
      assignmentViewModel.assignment.assignmentID,
      ...selectedOtherAssignmentIds,
    ];
    return assignmentViewModel.case.assignmentList.filter(
      (a) =>
        ids.includes(a.assignmentID) &&
        (a.stayOverTypeID === StayOverTypeEnum.HOTEL ||
          a.stayOverTypeID === StayOverTypeEnum.PRIVATE)
    );
  }, [assignmentViewModel, selectedOtherAssignmentIds]);

  const isNotArrivedOrArrived =
    state.cancelType === AssignmentCancelTypeEnum.CanceledNotArrived ||
    state.cancelType === AssignmentCancelTypeEnum.CanceledArrived;
  const isCanceledWithoutStarting =
    state.cancelType === AssignmentCancelTypeEnum.CanceledDayBefore ||
    state.cancelType === AssignmentCancelTypeEnum.CanceledNotStartedSameDay;
  const shouldAskKeepStayOver =
    isNotArrivedOrArrived && legsWithStayOverBeingCanceled.length > 0;
  const isSingleStayOverLeg = legsWithStayOverBeingCanceled.length === 1;

  // Svaret gäller alla uppdrag i listan, så visa vilka det är
  const stayOverLegList = (
    <ul style={{ margin: '5px 0', paddingLeft: 20 }}>
      {legsWithStayOverBeingCanceled.map((a) => (
        <li key={a.assignmentID}>
          {stayOverTypes[a.stayOverTypeID!]}: {a.fromAddress} - {a.toAddress} (
          {a.assignedTo?.name ?? 'Ej tilldelat'})
        </li>
      ))}
    </ul>
  );

  useEffect(() => {
    switch (state.cancelType) {
      case AssignmentCancelTypeEnum.CanceledArrived:
      case AssignmentCancelTypeEnum.CanceledNotArrived:
        setValue('endTime', undefined, { shouldTouch: true }); // don't set end time - assignee will do that from the app
        break;

      case AssignmentCancelTypeEnum.CanceledNotStartedSameDay:
        break;

      default:
        setValue('endTime', new Date(), { shouldTouch: true });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cancelType]);

  /**
   * Returns true if the cancel option is enabled for the current assignment
   */
  const optionEnabled = useCallback(
    (cancelOption: AssignmentCancelTypeEnum) => {
      if (
        (cancelOption === AssignmentCancelTypeEnum.CanceledNotArrived ||
          cancelOption === AssignmentCancelTypeEnum.CanceledArrived) &&
        blockArrivedCancelTypes
      ) {
        return false;
      }
      if (
        assignmentViewModel.assignment.assignmentStatusID ===
        AssignmentStatusEnum.Complete
      ) {
        return true;
      }
      switch (cancelOption) {
        case AssignmentCancelTypeEnum.CanceledDayBefore:
          // return true if assignment has not started yet, unless this is an intra-day purchase assignment not from or between stockholm, västra götaland, or skåne län
          if (
            assignmentViewModel.case.caseTypeID === CaseTypeEnum.Purchase ||
            assignmentViewModel.case.caseTypeID === CaseTypeEnum.Auction
          ) {
            // check the from address (is it within stockholm, gothenburg or malmö?)
            const fromCounty =
              assignmentViewModel.assignment.fromCounty?.areaName?.toLowerCase() ??
              '';
            const toCounty =
              assignmentViewModel.assignment.toCounty?.areaName?.toLowerCase() ??
              '';
            if (
              !(
                ['stockholm', 'västra götaland', 'skåne'].includes(
                  fromCounty
                ) &&
                ['stockholm', 'västra götaland', 'skåne'].includes(toCounty)
              )
            ) {
              // we are not in or between stockholm, västra götaland, or skåne - check if we are on an intra-day assignment
              if (
                assignmentViewModel.assignment.bookedTo.getDate() ===
                new Date().getDate()
              ) {
                // we are on an intra-day assignment
                return false;
              }
              // we are not on an intra-day assignment
              return true;
            }
          }
          return !assignmentViewModel.assignment.startTime;
        case AssignmentCancelTypeEnum.CanceledNotStartedSameDay: {
          // return true if assignment has not yet started, unless it is booked
          // to a future date - then only "Avbok dag före" should be selectable
          const isBookedToFutureDate = isAfter(
            startOfDay(assignmentViewModel.assignment.bookedTo),
            startOfDay(new Date())
          );
          return (
            !assignmentViewModel.assignment.startTime && !isBookedToFutureDate
          );
        }
        case AssignmentCancelTypeEnum.CanceledNotArrived:
          // return true if assignment has started and the status is lower than "Arrived"
          return (
            !!assignmentViewModel.assignment.startTime &&
            assignmentViewModel.assignment.assignmentStatusID <
              AssignmentStatusEnum.Arrived
          );
        case AssignmentCancelTypeEnum.CanceledArrived:
          // return true if assignment has started and the status is higher than "Arrived" but lower than "Completed"
          return (
            !!assignmentViewModel.assignment.startTime &&
            assignmentViewModel.assignment.assignmentStatusID >=
              AssignmentStatusEnum.Arrived &&
            assignmentViewModel.assignment.assignmentStatusID <
              AssignmentStatusEnum.Complete
          );
        case AssignmentCancelTypeEnum.CanceledRebooked:
          // return true if the assignment has not started yet
          return !assignmentViewModel.assignment.startTime;
        case AssignmentCancelTypeEnum.CanceledOther:
          // always enabled
          return true;

        default:
          return true;
      }
    },
    [assignmentViewModel, blockArrivedCancelTypes]
  );

  const handleOkClick = async () => {
    setIsCancelling(true);
    const linkedTradeInData = linkedTradeInToCancel
      ? {
          caseId: linkedTradeInToCancel.caseID,
          assignmentId: linkedTradeInToCancel.assignmentList[0].assignmentID,
        }
      : undefined;

    switch (state.cancelType) {
      case AssignmentCancelTypeEnum.CanceledArrived: // Bom anlänt
        onOkClick(
          assignmentViewModel.assignment.endTime
            ? AssignmentStatusEnum.Aborted
            : AssignmentStatusEnum.AbortedNotFinished, // set to AssignmentDeviationEnum.Aborted if we have an endtime, otherwise AssignmentDeviationEnum.AbortedNotFinished
          AssignmentDeviationEnum.Aborted,
          state.cancelType,
          false, // never auto-close bom-körningar
          true,
          selectedOtherAssignmentIds,
          assignmentViewModel.assignment.endTime, // don't change end time - assignee will do that from the app
          'Bomkörning',
          state.standbyEligible,
          linkedTradeInData,
          shouldAskKeepStayOver ? keepStayOver : undefined
        );
        break;
      case AssignmentCancelTypeEnum.CanceledOther: // Övrigt
        onOkClick(
          AssignmentStatusEnum.Canceled,
          AssignmentDeviationEnum.Canceled,
          state.cancelType,
          false,
          true,
          selectedOtherAssignmentIds,
          state.endTime,
          `Uppdrag avbokat - "${state.comment}"`,
          undefined,
          linkedTradeInData
        );
        break;
      case AssignmentCancelTypeEnum.CanceledRebooked: // Ombokning
        onOkClick(
          AssignmentStatusEnum.Canceled,
          AssignmentDeviationEnum.Canceled,
          state.cancelType,
          false,
          true,
          selectedOtherAssignmentIds,
          state.endTime,
          'Uppdrag ombokat',
          undefined,
          linkedTradeInData
        );
        break;
      case AssignmentCancelTypeEnum.CanceledDayBefore: // Avbok dagen innan
      case AssignmentCancelTypeEnum.CanceledNotStartedSameDay: // Avok ej påbörjat (samma dag)
        onOkClick(
          AssignmentStatusEnum.Canceled,
          AssignmentDeviationEnum.Canceled,
          state.cancelType,
          false,
          false,
          selectedOtherAssignmentIds,
          state.endTime,
          'Uppdrag avbokat',
          state.standbyEligible,
          linkedTradeInData
        );
        break;
      case AssignmentCancelTypeEnum.CanceledNotArrived: // Avbok ej anlänt
        onOkClick(
          assignmentViewModel.assignment.endTime
            ? AssignmentStatusEnum.Canceled
            : AssignmentStatusEnum.CanceledNotFinished, // set to AssignmentDeviationEnum.Canceled if we have an endtime, otherwise AssignmentDeviationEnum.CanceledNotFinished
          AssignmentDeviationEnum.Canceled,
          state.cancelType,
          false,
          true,
          selectedOtherAssignmentIds,
          assignmentViewModel.assignment.endTime, // don't set end time - assignee will do that from the app
          'Uppdrag avbokat',
          state.standbyEligible,
          linkedTradeInData,
          shouldAskKeepStayOver ? keepStayOver : undefined
        );
        break;

      default:
        break;
    }
  };

  return (
    <MyModal
      buttons={[
        {
          icon: <FontAwesomeIcon icon={faTrash} />,
          // loading: isCancelling,

          label: isCancelling ? 'Avbokar...' : 'Avboka',
          onClick: handleOkClick,
          disabled:
            isCancelling ||
            !!errors.cancelType ||
            !!errors.endTime ||
            !!errors.comment ||
            !!errors.standbyEligible ||
            (shouldHaveStandbyEligibleSet &&
              state.standbyEligible === undefined) ||
            (shouldAskKeepStayOver && keepStayOver === undefined) ||
            state.cancelType === -1,
        },
        {
          label: 'Avbryt',
          onClick: () => onCloseClick(),
        },
      ]}
      onClose={() => onCloseClick()}
      title="Avboka uppdrag"
    >
      <LabelWrap label="Typ av avbokning">
        <Select
          onChange={(ev) =>
            setValue('cancelType', Number(ev.currentTarget.value), {
              shouldValidate: true,
            })
          }
          value={state.cancelType}
        >
          <option
            key="default"
            onChange={() => setValue('cancelType', -1)}
            value={-1}
          >
            Välj typ av avbokning
          </option>
          {allCancelTypes
            .sort((a, b) => alphabeticCompare(cancelTypes[a], cancelTypes[b]))
            .map((cancelType) => (
              <option
                disabled={!optionEnabled(cancelType)}
                key={cancelType}
                value={cancelType}
              >
                {cancelTypes[cancelType]}
              </option>
            ))}
        </Select>
      </LabelWrap>

      {blockArrivedCancelTypes && (
        <Info>
          &quot;Avbok ej anlänt&quot; och &quot;Bom anlänt&quot; kan inte väljas
          eftersom den kopplade hemleveransen är avbokad.
        </Info>
      )}

      {state.cancelType !== undefined &&
        (state.cancelType === AssignmentCancelTypeEnum.CanceledDayBefore ||
          state.cancelType ===
            AssignmentCancelTypeEnum.CanceledNotStartedSameDay) && (
          <Info>
            OBS! Denna avbokningstyp är avsedd för uppdrag utan körtid. Start-
            och sluttid kommer automatiskt att sättas till vald sluttid nedan om
            uppdraget redan har en körtid på sig, annars blir båda tiderna Bokad
            till-tiden.
          </Info>
        )}

      {state.cancelType !== undefined &&
        (state.cancelType === AssignmentCancelTypeEnum.CanceledNotArrived ||
          state.cancelType ===
            AssignmentCancelTypeEnum.CanceledNotStartedSameDay ||
          state.cancelType === AssignmentCancelTypeEnum.CanceledArrived) && (
          <Info>
            {isTradeInCase ? (
              <LabelWrap label="Standby-tid berättigad">
                <Select small disabled value="false">
                  <option value="false">Nej</option>
                </Select>
                <p style={{ color: 'gray', fontStyle: 'italic', marginTop: 5 }}>
                  Inbyten kan inte berättiga standby-tid
                </p>
              </LabelWrap>
            ) : (
              <>
                <p
                  style={{
                    fontWeight: 'bold',
                    marginTop: 10,
                    marginBottom: 15,
                  }}
                >
                  Sätt berättigad till standby-tid om standby-tiden ska ersättas
                  med lön.
                </p>
                <LabelWrap label="Standby-tid berättigad">
                  <Select
                    small
                    onChange={(eve) => {
                      setValue(
                        'standbyEligible',
                        eve.currentTarget.value === 'true'
                      );
                    }}
                    value={
                      state.standbyEligible === undefined
                        ? ''
                        : state.standbyEligible
                          ? 'true'
                          : 'false'
                    }
                  >
                    <option value="">Välj</option>
                    <option value="true">Ja</option>
                    <option value="false">Nej</option>
                  </Select>
                </LabelWrap>
              </>
            )}
          </Info>
        )}
      {shouldAskKeepStayOver && (
        <Info>
          <LabelWrap
            label={
              isSingleStayOverLeg
                ? 'Övernattning – ska den stå kvar?'
                : `Övernattning på ${legsWithStayOverBeingCanceled.length} uppdrag – ska de stå kvar?`
            }
          >
            {stayOverLegList}
            <Select
              small
              onChange={(eve) =>
                setKeepStayOver(
                  eve.currentTarget.value === ''
                    ? undefined
                    : eve.currentTarget.value === 'true'
                )
              }
              value={keepStayOver === undefined ? '' : String(keepStayOver)}
            >
              <option value="">Välj</option>
              <option value="true">Ja, behåll</option>
              <option value="false">Nej</option>
            </Select>
          </LabelWrap>
          <p style={{ color: 'gray', fontStyle: 'italic', marginTop: 5 }}>
            Övernattningen ger nattraktamente i löneexporten.
            {!isSingleStayOverLeg &&
              ' Svaret gäller alla uppdragen ovan — behöver de olika svar, avboka dem var för sig.'}
          </p>
        </Info>
      )}

      {isCanceledWithoutStarting && legsWithStayOverBeingCanceled.length > 0 && (
        <Info>
          Övernattningen slås tillbaka till Nej eftersom uppdraget avbokas utan
          att ha påbörjats:
          {stayOverLegList}
        </Info>
      )}

      {assignmentViewModel.assignment.startTime &&
        state.cancelType !== undefined &&
        ![
          -1,
          AssignmentCancelTypeEnum.CanceledArrived,
          AssignmentCancelTypeEnum.CanceledNotArrived,
        ].includes(state.cancelType) && (
          <LabelWrap label="Sluttid för uppdraget">
            <Input
              onChange={(e) =>
                setValue('endTime', new Date(e.currentTarget.value))
              }
              type="datetime-local"
              value={state.endTime ? toInputDateTimeString(state.endTime) : ''}
            />
          </LabelWrap>
        )}

      <Info>
        <LabelWrap label={<strong>Valt uppdrag</strong>}>
          {assignmentViewModel.assignment.fromAddress} -{' '}
          {assignmentViewModel.assignment.toAddress}
        </LabelWrap>
        {otherAssignmentsWithinSameCase.length > 0 && (
          <LabelWrap
            label={<strong>Avboka övriga uppdrag i samma ärende</strong>}
          >
            <div style={{ gap: 10, display: 'flex', flexDirection: 'column' }}>
              {otherAssignmentsWithinSameCase.map((a) => (
                <Checkbox
                  key={a.assignmentID}
                  checked={
                    selectedOtherAssignmentIds?.findIndex(
                      (id) => id === a.assignmentID
                    ) !== -1
                  }
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setSelectedOtherAssignmentIds([
                        ...(selectedOtherAssignmentIds ?? []),
                        a.assignmentID,
                      ]);
                    } else {
                      setSelectedOtherAssignmentIds(
                        selectedOtherAssignmentIds?.filter(
                          (id) => id !== a.assignmentID
                        )
                      );
                    }
                  }}
                >
                  {a.fromAddress} - {a.toAddress} (
                  {a.assignedTo?.name ?? 'Ej tilldelat'})
                </Checkbox>
              ))}
            </div>
          </LabelWrap>
        )}
      </Info>

      {linkedTradeInToCancel && (
        <Info>
          <LabelWrap label={<strong>Kopplat inbyte</strong>}>
            {state.cancelType === AssignmentCancelTypeEnum.CanceledNotArrived ||
            state.cancelType === AssignmentCancelTypeEnum.CanceledArrived ? (
              <>
                Inbyte {linkedTradeInToCancel.registrationNumber} kommer att
                avbokas med typen &quot;Avbok ej påbörjat (samma dag)&quot; —
                &quot;Avbok ej anlänt&quot;/&quot;Bom anlänt&quot; kan inte
                sättas på ett inbyte vars hemleverans är avbokad (standby-tid är
                ej berättigad för inbyten)
              </>
            ) : (
              <>
                Inbyte {linkedTradeInToCancel.registrationNumber} kommer att
                avbokas med samma status (men standby-tid är ej berättigad för
                inbyten)
              </>
            )}
          </LabelWrap>
        </Info>
      )}

      {state.cancelType === AssignmentCancelTypeEnum.CanceledOther && (
        <LabelWrap label="Kommentar">
          <AutoSizedTextArea
            onChange={(e) => setValue('comment', e.currentTarget.value)}
            value={state.comment}
          />
        </LabelWrap>
      )}
    </MyModal>
  );
};

export default CancelCaseAssignmentModal;
